/**
 * VerificationGate Component
 * 
 * Mostra lo stato di accesso e permette di richiedere verifica.
 * 
 * FASE ATTUALE: Solo UI gate, no azioni operative.
 * 
 * Stati:
 * - Consultativo: Mostra CTA per verifica
 * - Verificato: Mostra badge "Accesso verificato"
 * - Richiesta in corso: Mostra form email
 * - Link inviato: Mostra messaggio di conferma
 */

import { useState } from 'react';
import { Shield, Mail, CheckCircle, Clock, Lock, Send, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';

/**
 * Compact banner per mostrare stato accesso nella pagina
 */
export function VerificationBanner({ 
  isVerified, 
  sessionMinutesRemaining,
  onRequestVerification,
  loading 
}) {
  if (loading) {
    return null;
  }

  if (isVerified) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle size={16} />
          <span className="text-sm font-medium">Accesso verificato</span>
        </div>
        <div className="flex items-center gap-2 text-green-600 text-xs">
          <Clock size={12} />
          <span>Valido per {sessionMinutesRemaining} min</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 flex items-center justify-between">
      <div className="flex items-center gap-2 text-amber-700">
        <Shield size={16} />
        <span className="text-sm">Alcune azioni richiedono verifica</span>
      </div>
      <Button 
        size="sm" 
        variant="outline"
        className="text-amber-700 border-amber-300 hover:bg-amber-100"
        onClick={onRequestVerification}
      >
        <Lock size={14} className="mr-1" />
        Verifica
      </Button>
    </div>
  );
}

/**
 * Card completa per richiedere verifica
 */
export function VerificationRequestCard({
  emailHint,
  onRequestMagicLink,
  requesting,
  linkRequested,
  error
}) {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setLocalError(null);
    setRateLimited(false);
    
    const result = await onRequestMagicLink(email);
    
    if (result.rate_limited) {
      setRateLimited(true);
      setLocalError(result.message);
    } else if (result.success) {
      setSubmitted(true);
    } else if (result.message) {
      setLocalError(result.message);
    }
  };

  if (linkRequested || submitted) {
    return (
      <Card className="bg-green-50 border-green-200">
        <CardContent className="pt-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail size={24} className="text-green-600" />
            </div>
            <h3 className="font-semibold text-green-800 mb-2">
              Controlla la tua email
            </h3>
            <p className="text-sm text-green-700 mb-4">
              Se l'indirizzo è corretto, riceverai un link di verifica.
              Controlla anche la cartella spam.
            </p>
            <p className="text-xs text-green-600">
              Il link è valido per 30 minuti e può essere usato una sola volta.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-umbria-stone/20">
      <CardContent className="pt-6">
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-umbria-olive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield size={24} className="text-umbria-olive" />
          </div>
          <h3 className="font-semibold text-umbria-charcoal mb-2">
            Verifica richiesta
          </h3>
          <p className="text-sm text-umbria-muted">
            Per accedere alle azioni sensibili, verifica la tua identità.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-umbria-charcoal mb-1">
              La tua email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={emailHint ? `es. ${emailHint}` : 'email@esempio.com'}
              className="w-full px-3 py-2 border border-umbria-stone/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-umbria-olive/50"
              required
              disabled={requesting || rateLimited}
            />
            <p className="text-xs text-umbria-muted mt-1">
              Inserisci l'email associata al tuo viaggio
            </p>
          </div>

          {(error || localError) && (
            <div className={`flex items-center gap-2 text-sm ${rateLimited ? 'text-amber-600 bg-amber-50' : 'text-red-600'} p-2 rounded`}>
              <AlertCircle size={14} />
              <span>{localError || error}</span>
            </div>
          )}

          <Button 
            type="submit" 
            className="w-full bg-umbria-olive hover:bg-umbria-olive/90"
            disabled={requesting || !email.trim() || rateLimited}
          >
            {requesting ? (
              <>
                <Clock size={16} className="mr-2 animate-spin" />
                Invio in corso...
              </>
            ) : rateLimited ? (
              <>
                <Clock size={16} className="mr-2" />
                Attendi prima di riprovare
              </>
            ) : (
              <>
                <Send size={16} className="mr-2" />
                Invia link di verifica
              </>
            )}
          </Button>
        </form>

        <div className="mt-4 pt-4 border-t border-umbria-stone/10">
          <p className="text-xs text-umbria-muted text-center">
            Riceverai un link sicuro via email valido per 30 minuti.
            <br />
            Non condividere il link con nessuno.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Badge inline per stato accesso
 */
export function AccessLevelBadge({ isVerified, compact = false }) {
  if (isVerified) {
    return (
      <Badge className="bg-green-100 text-green-700 gap-1">
        <CheckCircle size={12} />
        {!compact && 'Verificato'}
      </Badge>
    );
  }

  return (
    <Badge variant="outline" className="text-umbria-muted gap-1">
      <Shield size={12} />
      {!compact && 'Consultazione'}
    </Badge>
  );
}

/**
 * Wrapper per azioni sensibili
 * Mostra il contenuto solo se verificato, altrimenti mostra CTA
 */
export function SensitiveActionGate({ 
  isVerified, 
  onRequestVerification,
  children,
  title = "Azione protetta",
  description = "Questa azione richiede verifica dell'identità."
}) {
  if (isVerified) {
    return children;
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
      <Lock size={24} className="text-gray-400 mx-auto mb-2" />
      <p className="font-medium text-gray-700 mb-1">{title}</p>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <Button 
        size="sm" 
        variant="outline"
        onClick={onRequestVerification}
      >
        <Shield size={14} className="mr-1" />
        Verifica per continuare
      </Button>
    </div>
  );
}

export default {
  VerificationBanner,
  VerificationRequestCard,
  AccessLevelBadge,
  SensitiveActionGate
};
